<template>
  <div class="currency-selector">
    <CurrencyFlag :code="code" />
    <el-select
      v-model="selectedCode"
      class="currency-selector__select"
      :loading="currencyStore.loading"
      :disabled="currencyStore.locked || readonly"
      @change="onChange"
    >
      <el-option v-for="item in options" :key="item.code" :label="item.code" :value="item.code">
        <span class="currency-selector__option">
          <CurrencyFlag :code="item.code" />
          <span>{{ item.code }}</span>
        </span>
      </el-option>
    </el-select>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useCurrencyStore } from '@/stores/CurrencyStore'
import { captureException } from '@/utils/SentryUtils'
import CurrencyFlag from '@/components/CurrencyFlag.vue'

// readonly: show the current currency without allowing a switch (e.g. order pages).
withDefaults(defineProps<{ readonly?: boolean }>(), {
  readonly: false
})

const currencyStore = useCurrencyStore()

const code = computed(() => currencyStore.currentCurrency)

// Before the backend list loads, seed a single option matching the persisted/default
// currency so the select never renders empty.
const options = computed(() =>
  currencyStore.currencyList.length
    ? currencyStore.currencyList
    : [
        {
          code: code.value,
          title: code.value,
          symbolLeft: '',
          symbolRight: '',
          decimalPlace: 2,
          value: 1
        }
      ]
)

// Setter is intentionally a no-op: the store's currentCurrency is the single source of
// truth and is updated (optimistically) inside setCurrency, which is what should drive
// the displayed value.
const selectedCode = computed({
  get: () => code.value,
  set: () => {}
})

onMounted(() => {
  currencyStore.fetchCurrencies()
})

async function onChange(code: string) {
  try {
    await currencyStore.setCurrency(code)
    // Currency-aware queries only send the current currency on their own initial
    // fetch - switching currency alone doesn't make any of them refetch, so
    // whatever's already on the page would otherwise stay priced in the old
    // currency even though the selector now shows the new one. A reload is the
    // simplest way to get every price-bearing query on the page to refetch. This
    // lives here (not in the store) because setCurrency() is also used to
    // programmatically sync currency mid-flow (cart.vue, user/bookings), where a
    // reload would interrupt in-progress state - only this explicit user pick
    // should trigger one.
    window.location.reload()
  } catch (error) {
    captureException(error, { tags: { component: 'CurrencySelector' } })
  }
}
</script>

<style scoped lang="scss">
.currency-selector {
  height: 2.8rem;
  padding: 0 1rem 0 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 1.4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: inherit;
}

.currency-selector :deep(.currency-selector__select) {
  width: 4rem;
}

.currency-selector :deep(.currency-selector__select) .el-select__wrapper {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 0 0 0.4rem !important;
  min-height: auto !important;
  color: inherit !important;
  font-size: 1.2rem !important;
  font-weight: 700 !important;
  line-height: 2rem !important;
}

.currency-selector :deep(.currency-selector__select) .el-select__selected-item,
.currency-selector :deep(.currency-selector__select) .el-select__placeholder {
  color: inherit !important;
}

.currency-selector :deep(.currency-selector__select) .el-select__caret {
  color: inherit !important;
}

.currency-selector :deep(.currency-selector__select) .el-select__suffix {
  display: none;
}

.currency-selector__option {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
}
</style>
